import React from "react";
import { useThemeStore } from "../../stores/themeStore";

interface LogCardHeaderProps {
  title: string;
  variant?: "primary" | "secondary";
}

export const LogCardHeader: React.FC<LogCardHeaderProps> = ({
  title,
  variant = "primary",
}) => {
  return (
    <div
      className={`
        text-sm font-semibold font-outfit mb-1
        ${variant === "primary" ? "text-base-content" : "text-base-content/80"}
      `}
    >
      {title}
    </div>
  );
};

interface LogCardNoteProps {
  text: string;
  marginLeft?: number;
  marginTop?: number;
}

export const LogCardNote: React.FC<LogCardNoteProps> = ({
  text,
  marginLeft = 0,
  marginTop = 0.5,
}) => {
  return (
    <p
      className="text-[13px] text-base-content/70 font-grotesk leading-relaxed whitespace-pre-line mt-1"
      style={{
        marginLeft: `${marginLeft * 0.25}rem`,
        marginTop: `${marginTop * 0.25}rem`,
      }}
    >
      {text}
    </p>
  );
};

interface LogCardMetricProps {
  value: string | number;
  label: string;
  variant?: "large" | "small";
}

export const LogCardMetric: React.FC<LogCardMetricProps> = ({
  value,
  label,
  variant = "small",
}) => {
  return (
    <div className="flex items-center justify-between gap-3 bg-base-200 rounded-md px-3 py-2 mb-1.5">
      <span className="text-xs text-base-content/60 font-geist">{label}</span>
      <span
        className={`
          font-bold font-geist text-base-content
          ${variant === "large" ? "text-base" : "text-sm"}
        `}
      >
        {value}
      </span>
    </div>
  );
};

interface LogCardChipProps {
  label: string;
  variant?: "default" | "primary" | "success" | "warning";
  size?: "small" | "medium";
}

export const LogCardChip: React.FC<LogCardChipProps> = ({
  label,
  variant = "default",
  size = "small",
}) => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === "peakx-dark";

  const baseClasses = `inline-flex items-center px-2 py-0.5 rounded-md font-medium font-geist ${
    size === "small" ? "text-[11px]" : "text-xs"
  }`;

  const variantClasses = {
    primary: `bg-primary/15 ${isDark ? "text-blue-300" : "text-blue-950"}`,
    success: `bg-green-500/15 ${isDark ? "text-green-400" : "text-green-700"}`,
    warning: `bg-yellow-500/15 ${isDark ? "text-yellow-400" : "text-yellow-700"}`,
    default: "bg-base-400 text-base-content",
  };

  return (
    <span className={`${baseClasses} ${variantClasses[variant]}`}>{label}</span>
  );
};

interface LogCardMetricsRowProps {
  children: React.ReactNode;
  gap?: number;
}

export const LogCardMetricsRow: React.FC<LogCardMetricsRowProps> = ({
  children,
  gap = 0.5,
}) => {
  return (
    <div
      className="flex flex-wrap items-center"
      style={{ gap: `${gap * 0.25}rem` }}
    >
      {children}
    </div>
  );
};
